/**
 * Hook for managing tutor conversations and messages in the bubble chat
 * Modified version of useTutor that doesn't navigate to the tutor page
 */
import { ApiErrorResponse, LimitErrorType } from '@shared/types/api.types';
import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { TutorService } from '../services/tutorService';
import { StreamChunk, TutorConversation, TutorMessage } from '../types';

export const useBubbleTutor = (conversationId?: string) => {
  const [conversations, setConversations] = useState<TutorConversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<TutorConversation | null>(null);
  const [messages, setMessages] = useState<TutorMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Fetch all conversations
  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await TutorService.getConversations();
      setConversations(data);
      setError(null);
    } catch (err: any) {
      console.error('Error fetching conversations:', err);

      if (err?.code === 'SUBSCRIPTION_LIMIT_REACHED') {
        setError('subscription_limit');
      } else {
        setError('Failed to load conversations');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch a specific conversation and its messages
  const fetchConversation = useCallback(async (id: string) => {
    try {
      setLoading(true);
      const [conversation, messagesData] = await Promise.all([
        TutorService.getConversation(id),
        TutorService.getMessages(id),
      ]);
      setCurrentConversation(conversation);
      setMessages(messagesData);
      setError(null);
    } catch (err) {
      console.error('Error fetching conversation:', err);
      setError('Failed to load conversation');
      toast.error('Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, []);

  // Create a new conversation without navigating
  const createConversation = useCallback(async (title?: string) => {
    try {
      setLoading(true);
      const newConversation = await TutorService.createConversation(title);
      setConversations((prev) => [newConversation, ...prev]);
      // No navigation here, just return the conversation
      return newConversation;
    } catch (err: any) {
      console.error('Error creating conversation:', err.response.data);

      // Check if this is a limit exceeded error (HTTP 429)
      if (err.response.data.error === LimitErrorType.DAILY_LIMIT_EXCEEDED) {
        const errorData = err.response.data as ApiErrorResponse;
        setError(errorData.message);
      } else {
        toast.error('Failed to create new conversation');
      }
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);


  // Helper function to send a message to a specific conversation
  const sendMessageToConversation = useCallback(async (convId: string, message: string) => {
    try {
      setSendingMessage(true);
      setStreamingContent('');

      // Add user message to the UI immediately
      const userMessage: TutorMessage = {
        id: `temp-${Date.now()}`,
        conversationId: convId,
        content: message,
        role: 'user',
        createdAt: new Date(),
      };

      setMessages((prev) => [...prev, userMessage]);

      // Start streaming response
      const response = await TutorService.sendMessage(convId, message);

      if (!response.ok) {
        // Check if this is a rate limit error
        if (response.status === 429) {
          try {
            const errorData = await response.json() as ApiErrorResponse;
            if (errorData.error === LimitErrorType.DAILY_LIMIT_EXCEEDED) {
              setError(errorData.message);
              throw errorData;
            }
          } catch (parseError) {
            console.error('Error parsing limit error response:', parseError);
          }
        }
        throw new Error(`Error: ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      // Add assistant message placeholder
      const assistantMessage: TutorMessage = {
        id: `temp-assistant-${Date.now()}`,
        conversationId: convId,
        content: '',
        role: 'assistant',
        createdAt: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // Process the stream
      let done = false;
      let accumulatedContent = '';

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;

        if (done) break;

        // Convert the chunk to text
        const chunk = new TextDecoder().decode(value);
        const lines = chunk.split('\n').filter(line => line.trim());

        for (const line of lines) {
          try {
            const parsedChunk: StreamChunk = JSON.parse(line);

            if (parsedChunk.type === 'chunk' && parsedChunk.content) {
              accumulatedContent += parsedChunk.content;
              setStreamingContent(accumulatedContent);

              // Update the assistant message with the new content
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantMessage.id
                    ? { ...msg, content: accumulatedContent }
                    : msg
                )
              );
            } else if (parsedChunk.type === 'error') {
              throw new Error(parsedChunk.content || 'Unknown error');
            }
          } catch (err) {
            console.error('Error parsing chunk:', err, line);
          }
        }
      }

      // First update with a temporary title to show progress
      if (currentConversation) {
        const tempTitle = messages.length <= 2 ?
          `${message.substring(0, 40)}${message.length > 40 ? '...' : ''}` :
          currentConversation.title;

        // Update with temporary title immediately
        const tempConversation = { ...currentConversation, title: tempTitle };
        setCurrentConversation(tempConversation);

        // Update in the conversations list
        setConversations(prev =>
          prev.map(conv => conv.id === convId ? tempConversation : conv)
        );
      }

      setTimeout(async () => {
        try {
          const singleConversation = await TutorService.getConversation(convId);

          setCurrentConversation(singleConversation);

          const allConversations = await TutorService.getConversations();

          setConversations(allConversations.map(conv =>
            conv.id === convId ? singleConversation : conv
          ));
        } catch (error) {
          console.error('Error updating conversation after message:', error);
        }
      }, 3000);

    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSendingMessage(false);
    }
  }, []);
  // Send a message and handle streaming response
  const sendMessage = useCallback(async (message: string) => {
    try {
      if (!currentConversation) {
        // Create a new conversation if none exists
        setSendingMessage(true); // Set loading state immediately
        try {
          const newConversation = await createConversation("New Conversation");

          // Ensure we have the conversation before proceeding
          setCurrentConversation(newConversation);

          // Now send the message to the new conversation
          await sendMessageToConversation(newConversation.id, message);
        } catch (err: any) {
          // Check if this is a limit exceeded error (HTTP 429)
          if (err.response && err.response.status === 429) {
            // Already handled in createConversation
            setSendingMessage(false);
            return;
          }
          throw err;
        }
      } else {
        // Use existing conversation
        try {
          await sendMessageToConversation(currentConversation.id, message);
        } catch (err: any) {
          console.error('Error sending message:', err);
          // Check if this is a limit exceeded error (HTTP 429)
          if (err.response && err.response.status === 429) {
            const errorData = err.response.data;
            setError(errorData.error);
            setSendingMessage(false);
            return;
          }
          throw err;
        }
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setSendingMessage(false); // Reset loading state on error
    }
  }, [currentConversation, createConversation, sendMessageToConversation]);

  // Update conversation title
  const updateConversationTitle = useCallback(async (id: string, title: string) => {
    try {
      await TutorService.updateConversationTitle(id, title);

      // Update local state
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === id ? { ...conv, title } : conv
        )
      );

      if (currentConversation?.id === id) {
        setCurrentConversation((prev) =>
          prev ? { ...prev, title } : null
        );
      }

      toast.success('Conversation title updated');
    } catch (err) {
      console.error('Error updating conversation title:', err);
      toast.error('Failed to update conversation title');
    }
  }, [currentConversation]);

  // Delete a conversation
  const deleteConversation = useCallback(async (id: string) => {
    try {
      await TutorService.deleteConversation(id);

      // Update local state
      setConversations((prev) =>
        prev.filter((conv) => conv.id !== id)
      );

      if (currentConversation?.id === id) {
        setCurrentConversation(null);
        setMessages([]);
      }

      toast.success('Conversation deleted');
    } catch (err) {
      console.error('Error deleting conversation:', err);
      toast.error('Failed to delete conversation');
    }
  }, [currentConversation]);

  // Load initial data only once when the hook is first initialized
  useEffect(() => {
    // Only fetch if we don't already have conversations
    if (conversations.length === 0) {
      fetchConversations();
    }
  }, []);

  // Load conversation data when ID changes
  useEffect(() => {
    if (conversationId) {
      fetchConversation(conversationId);
    } else {
      setCurrentConversation(null);
      setMessages([]);
    }
  }, [conversationId, fetchConversation]);

  return {
    conversations,
    currentConversation,
    messages,
    loading,
    sendingMessage,
    streamingContent,
    error,
    fetchConversations,
    fetchConversation,
    createConversation,
    sendMessage,
    updateConversationTitle,
    deleteConversation,
  };
};
